import { nodeConfig } from '@scripts/mixins';
import { Disclosure } from '@scripts/modules/disclosure';
import { verticalKeyNavigationHandler } from '@scripts/helpers/key-navigation';

const NAMESPACE = 'nav-disclosure';
const DEFAULT_SELECTOR = '[data-js-nav-disclosure]';

/**
 * @typedef {Object} NavDisclosureSettings
 * @property {boolean} [grouped=false] - Auto-close siblings when one opens.
 * @property {boolean} [keyboardNavigation=true] - Arrow key navigation within children.
 * @property {string|null} [mediaQuery=null] - CSS media query passed to each Disclosure.
 */
const DEFAULT_SETTINGS = {
	grouped: false,
	keyboardNavigation: true,
	mediaQuery: null
};

/**
 * Disclosure-based navigation using Disclosure module.
 * @fires nav-disclosure:open
 * @fires nav-disclosure:close
 */
class NavDisclosure {
	constructor(node, config) {
		this.elNode = node;
		this.config = config;
		this.elItems = this.elNode.querySelectorAll(`[data-js-${NAMESPACE}-item]`);
		this.disclosures = [];
	}

	init() {
		this.elItems.forEach((elItem) => {
			const elButton = elItem.querySelector(`[data-js-${NAMESPACE}-button]`);

			if (!elButton) return;

			const disclosure = new Disclosure(elItem, {
				namespace: NAMESPACE,
				collapsible: true,
				closeOnEscape: true,
				closeOnBlur: true,
				closeOnOutsideClick: true,
				restoreFocusOnClose: true,
				keyboardNavigation: false,
				mediaQuery: this.config.mediaQuery
			});

			disclosure.init();
			elButton.setAttribute('aria-haspopup', 'true');
			this.disclosures.push(disclosure);
		});

		this._bindUI();
	}

	destroy() {
		this._unbindUI();
		this.disclosures.forEach((d) => d.destroy());
		this.disclosures = [];
	}

	_bindUI() {
		this._onButtonKeydown = this._onButtonKeydown.bind(this);

		if (this.config.grouped) {
			this._onOpen = this._onOpen.bind(this);
			this.elNode.addEventListener(`${NAMESPACE}:open`, this._onOpen);
		}

		this.disclosures.forEach((d) => {
			d.elButton.addEventListener('keydown', this._onButtonKeydown);
		});

		if (this.config.keyboardNavigation) {
			this._onChildKeydown = this._onChildKeydown.bind(this);

			this.disclosures.forEach((d) => {
				d.elPanel.addEventListener('keydown', this._onChildKeydown);
			});
		}
	}

	_unbindUI() {
		if (this._onOpen) {
			this.elNode.removeEventListener(`${NAMESPACE}:open`, this._onOpen);
		}

		this.disclosures.forEach((d) => {
			d.elButton.removeEventListener('keydown', this._onButtonKeydown);

			if (this._onChildKeydown) {
				d.elPanel.removeEventListener('keydown', this._onChildKeydown);
			}
		});
	}

	/** Close sibling disclosures when one opens. */
	_onOpen(event) {
		const opened = event.detail.instance;

		this.disclosures.forEach((d) => {
			if (d !== opened && d.isOpen()) {
				d.close(false);
			}
		});
	}

	/** ArrowUp/ArrowDown on toggle opens and focuses first/last child link. */
	_onButtonKeydown(event) {
		const key = event.key.toLowerCase();

		if (!['arrowup', 'arrowdown'].includes(key)) return;

		event.preventDefault();

		const disclosure = this.disclosures.find((d) => d.elButton === event.currentTarget);

		if (!disclosure) return;

		if (!disclosure.isOpen()) {
			disclosure.open();
		}

		const childLinks = disclosure.elPanel.querySelectorAll(`[data-js-${NAMESPACE}-child-link]`);

		if (!childLinks.length) return;

		const index = key === 'arrowdown' ? 0 : childLinks.length - 1;

		childLinks[index].focus();
	}

	/** Vertical arrow key navigation between child links. */
	_onChildKeydown(event) {
		const childLink = event.target.closest(`[data-js-${NAMESPACE}-child-link]`);

		if (!childLink) return;

		const panel = event.currentTarget;
		const childItems = panel.querySelectorAll(`[data-js-${NAMESPACE}-child-item]`);

		verticalKeyNavigationHandler({
			event,
			items: childItems,
			getTrigger: (item) => item.querySelector(`[data-js-${NAMESPACE}-child-link]`),
			loop: false
		});
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new NavDisclosure(node, config);

		module.init();

		return module;
	});
}
